import { useEffect, useState } from "react";
import { api, CLASSIFICATIONS } from "../lib/api";
import { PageHeader } from "../components/Layout";

export default function WorkRoles() {
  const [roles, setRoles] = useState([]);
  const [depts, setDepts] = useState([]);

  useEffect(() => {
    Promise.all([api.get("/roles"), api.get("/departments")]).then(([r, d]) => { setRoles(r.data); setDepts(d.data); });
  }, []);

  const deptByKey = Object.fromEntries(depts.map((d) => [d.key, d]));

  return (
    <div className="canvas-grain min-h-screen pb-16" data-testid="work-roles-page">
      <PageHeader overline="Work & Role Redesign" title="Role Impact Analysis"
        subtitle="Task-level classification per role. Capacity figures are redeployment potential — never headcount-reduction recommendations. Executive options: capacity, redeployment, growth, service or risk." />
      <div className="px-8 mb-5 bg-white border border-black/10 rounded-sm p-4 flex flex-wrap gap-3">
        {Object.values(CLASSIFICATIONS).map((c) => (
          <span key={c.label} className="flex items-center gap-1.5 text-[11px]"><span className="w-3 h-3 rounded-sm" style={{ background: c.color }} />{c.label}</span>
        ))}
      </div>
      <div className="px-8 grid grid-cols-1 lg:grid-cols-2 gap-5">
        {roles.map((r) => {
          const dept = deptByKey[r.department_key];
          return (
            <div key={r.id} className="bg-white border border-black/10 rounded-sm p-5" data-testid={`role-card-${r.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-display font-bold text-lg tracking-tight">{r.title}</h3>
                  <p className="text-[12px]" style={{ color: dept?.color }}>{dept?.name} · {r.headcount} people</p>
                  {r.reports_to && <p className="text-[11px] text-black/50 mt-0.5" data-testid={`role-reports-to-${r.id}`}>↳ Reports to {r.reports_to}</p>}
                  {r.source_document && <p className="text-[10px] mt-0.5" style={{ color: "#A855F7" }} data-testid={`role-source-${r.id}`}>● AI inference · from {r.source_document}</p>}
                </div>
                <div className="text-right">
                  <span className="font-display text-2xl font-black text-[#D4AF37]">{r.capacity_released_pct}%</span>
                  <p className="text-[10px] text-black/50 leading-tight">capacity release<br />potential</p>
                </div>
              </div>
              <div className="flex h-5 mt-4 rounded-sm overflow-hidden" title="Task mix">
                {r.tasks.map((t) => (
                  <div key={t.name} style={{ width: `${t.pct_time}%`, background: CLASSIFICATIONS[t.classification]?.color }}
                    title={`${t.name} — ${CLASSIFICATIONS[t.classification]?.label} (${t.pct_time}%)`} />
                ))}
              </div>
              <div className="mt-3 space-y-1.5">
                {r.tasks.map((t) => (
                  <div key={t.name} className="flex items-center justify-between text-[12px]">
                    <span className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: CLASSIFICATIONS[t.classification]?.color }} />{t.name}</span>
                    <span className="text-black/50 font-mono">{t.pct_time}% · {CLASSIFICATIONS[t.classification]?.label}</span>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-3 mt-4 pt-3 border-t border-black/8 text-[12px]">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.15em] text-green-700 mb-1">Emerging skills</p>
                  {r.skills_emerging.map((s) => <p key={s}>+ {s}</p>)}
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.15em] text-red-600 mb-1">Declining skills</p>
                  {r.skills_declining.map((s) => <p key={s} className="text-black/60">− {s}</p>)}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
